import { useCallback, useEffect, useState } from 'react';
import { getText } from 'i18n/intl';
import api from 'services/api.service';
import { selectActivityCreateTemplates } from 'slices/objectGenerationTemplate.slice';
import { selectObjectTypesMap } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import { Alert, Icon, TEDrawerSection } from '@timeedit/ui-components';
import { Switch, Table, Tooltip } from '@timeedit/ui-components/antd';
import './EditMembersAndRelations.scss';

type TItem = {
  id: number;
  name: string;
  [key: string]: unknown;
};

type TAPIResult<T> = {
  results: T[];
};

type Props = {
  title: string;
  explanationText: string;
  endpoint: string;
  typeKey: string;
  value: number[];
  activityCreateTemplateId: string | null;
  groupObjectType: number | null;
  onChange: (enabledIds: number[]) => void;
};

const EditMembersAndRelations = ({
  title,
  explanationText,
  endpoint,
  typeKey,
  value,
  activityCreateTemplateId,
  groupObjectType,
  onChange,
}: Props) => {
  const [items, setItems] = useState<TItem[]>([]);
  const [loadedIds, setLoadedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [missingIds, setMissingIds] = useState<number[]>([]);

  const activityCreateTemplates = useAppSelector(selectActivityCreateTemplates);
  const objectTypes = useAppSelector(selectObjectTypesMap);

  /* In order to be selectable, a membership config or a relation config needs to include both the currently 
  selected object type of the group config (i.e. something like "student set group") and at least one object 
  type within the activity create template's object types. I.e. something like "course occurrence".
  That's what we check here - for both relations and memberships. */
  const memberOrRelationConfigDisabled = useCallback(
    (item: TItem | undefined = undefined) => {
      if (!activityCreateTemplateId) {
        return false;
      }

      const activityTemplate = activityCreateTemplates.find(
        (act) => act._id === activityCreateTemplateId
      );
      const objectExtIds = [
        ...(activityTemplate?.objects?.map((obj) => obj.extId) ?? []),
        activityTemplate?.primaryObject.extId,
        activityTemplate?.activityType.extId,
      ];

      const relOrMemTypeSelectable = (membOrRelTypes: { type: number }[]) => {
        let memberOrRelationExistsInACT = false;
        let groupObjectTypeExistsInMemberOrRelations = false;
        for (const membOrRelType of membOrRelTypes) {
          if (
            objectExtIds.includes(objectTypes[membOrRelType.type].extId) &&
            membOrRelType.type !== groupObjectType
          ) {
            memberOrRelationExistsInACT = true;
          }

          if (membOrRelType.type === groupObjectType) {
            groupObjectTypeExistsInMemberOrRelations = true;
          }
          if (
            memberOrRelationExistsInACT &&
            groupObjectTypeExistsInMemberOrRelations
          ) {
            return true;
          }
        }

        return false;
      };

      if (item && 'memberTypes' in item && Array.isArray(item.memberTypes)) {
        if (relOrMemTypeSelectable(item.memberTypes)) {
          return false;
        }
      }

      if (
        item &&
        'relationTypes' in item &&
        Array.isArray(item.relationTypes)
      ) {
        if (relOrMemTypeSelectable(item.relationTypes)) {
          return false;
        }
      }

      return true;
    },
    [
      activityCreateTemplates,
      objectTypes,
      activityCreateTemplateId,
      groupObjectType,
    ]
  );

  useEffect(() => {
    setLoading(true);
    api
      .get<TAPIResult<TItem>>({ endpoint })
      .then((res) => {
        const loaded: TItem[] = res.results ?? [];
        setItems(
          loaded.sort((a, b) => (memberOrRelationConfigDisabled(a) ? 1 : -1))
        );
        const loadedIdsSet = new Set(loaded.map((item) => item.id));
        setLoadedIds(loadedIdsSet);
        setMissingIds(value.filter((id) => !loadedIdsSet.has(id)));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [endpoint, value.filter]);

  // Re-sort the already loaded items whenever the criteria that determine
  // whether an item is selectable change, so disabled items move to the bottom.
  useEffect(() => {
    setItems((prevItems) =>
      [...prevItems].sort((a, b) =>
        memberOrRelationConfigDisabled(a) ? 1 : -1
      )
    );
  }, [memberOrRelationConfigDisabled]);

  const handleToggle = (id: number, checked: boolean) => {
    // Only values which are loadable and not disabled should be considered for the new value. All the rest: remove
    const usableValues = value.filter(
      (v) =>
        loadedIds.has(v) &&
        !memberOrRelationConfigDisabled(items?.find((item) => item.id === v))
    );
    if (checked) {
      onChange([...usableValues, id]);
    } else {
      onChange(usableValues.filter((v) => v !== id));
    }
  };

  const columns = [
    {
      title: '',
      key: 'toggle',
      dataIndex: 'id',
      width: 60,
      render: (id: number, record: TItem) => {
        if (memberOrRelationConfigDisabled(record)) {
          return (
            <Tooltip
              placement="topLeft"
              title={getText(
                'group_creation_missing_memb_or_rel_cannot_bel_selected'
              )}
            >
              <Switch size="small" checked={false} disabled />
            </Tooltip>
          );
        }

        return (
          <Switch
            size="small"
            checked={value.includes(id)}
            onChange={(checked) => handleToggle(id, checked)}
          />
        );
      },
    },
    {
      title: getText('name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: getText('type'),
      dataIndex: typeKey,
      key: 'type',
      render: (typeValue: string) => getText(typeValue) || typeValue,
    },
  ];

  return (
    <TEDrawerSection label={title}>
      <p>{explanationText}</p>
      {missingIds.length > 0 && (
        <Alert
          showIcon
          type="error"
          title={`${getText('group_creation_missing_memb_or_rel')} ${missingIds.join(', ')}`}
        />
      )}
      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        pagination={false}
        loading={loading}
        size="small"
      />
    </TEDrawerSection>
  );
};

export default EditMembersAndRelations;
